import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useGLTF, useProgress } from '@react-three/drei';
import { Group, Mesh, MeshStandardMaterial, Object3D, Material, Texture } from 'three';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * 五月湾游艇模型组件属性接口
 */
interface WuYuanWanModelProps {
  /** 模型加载完成回调 */
  onLoad?: () => void;
  /** 模型可见性控制 */
  visible?: boolean;
  /** 加载进度回调 */
  onLoadProgress?: (progress: number) => void;
  /** 错误处理回调 */
  onError?: (error: Error) => void;
  /** 动画启用控制 */
  enableAnimation?: boolean;
  /** 材质配置 */
  materialConfig?: {
    roughness?: number;
    metalness?: number;
    envMapIntensity?: number;
    clearcoat?: number;
    clearcoatRoughness?: number;
  };
}

// 安全地释放纹理资源
const disposeTexture = (texture: Texture | null) => {
  if (texture && texture.dispose) {
    texture.dispose();
  }
};

// 安全地释放材质资源
const disposeMaterial = (material: Material) => {
  if (material instanceof MeshStandardMaterial) {
    // 释放标准材质的纹理
    disposeTexture(material.map);
    disposeTexture(material.lightMap);
    disposeTexture(material.bumpMap);
    disposeTexture(material.normalMap);
    disposeTexture(material.envMap);
  }
  material.dispose();
};

// 模型路径常量
const MODEL_PATH = '/wuyuanwan11.glb';

/**
 * 五月湾游艇3D模型组件
 * 负责加载和渲染游艇模型，支持材质配置和动画效果
 */
export const WuYuanWanModel = ({
  onLoad,
  visible = true,
  onLoadProgress,
  onError,
  enableAnimation = true,
  materialConfig = {
    roughness: 0.7,
    metalness: 0.3,
    envMapIntensity: 1
  }
}: WuYuanWanModelProps) => {
  const group = useRef<Group>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  
  // 使用 useProgress 钩子来跟踪加载进度
  const { progress, errors } = useProgress();
  
  // 监听加载进度
  useEffect(() => {
    console.log('加载进度:', progress);
    onLoadProgress?.(progress);
  }, [progress, onLoadProgress]);

  // 监听加载错误
  useEffect(() => {
    if (errors.length > 0) {
      const errorMessage = errors.join(', ');
      console.error('模型加载错误:', errorMessage);
      setLoadError(new Error(errorMessage));
      onError?.(new Error(errorMessage));
    }
  }, [errors, onError]);
  
  // 使用 useGLTF 加载模型
  console.log('尝试加载模型:', MODEL_PATH);
  const { scene } = useGLTF(MODEL_PATH);
  console.log('模型加载完成:', scene);

  // 使用 useMemo 缓存克隆的场景
  const clonedScene = useMemo(() => {
    if (!scene) {
      console.error('场景为空');
      return null;
    }
    
    try {
      const cloned = scene.clone();
      console.log('场景克隆成功:', cloned);
      return cloned;
    } catch (err) {
      console.error('克隆场景失败:', err);
      const error = new Error('克隆场景时发生错误');
      setLoadError(error);
      onError?.(error);
      return null;
    }
  }, [scene, onError]);

  // 材质优化处理函数
  const optimizeMaterial = useCallback((material: MeshStandardMaterial) => {
    material.roughness = materialConfig.roughness ?? 0.7;
    material.metalness = materialConfig.metalness ?? 0.3;
    material.envMapIntensity = materialConfig.envMapIntensity ?? 1;
    
    // 添加清漆效果（如果支持）
    if ('clearcoat' in material && materialConfig.clearcoat !== undefined) {
      (material as any).clearcoat = materialConfig.clearcoat;
    }
    if ('clearcoatRoughness' in material && materialConfig.clearcoatRoughness !== undefined) {
      (material as any).clearcoatRoughness = materialConfig.clearcoatRoughness;
    }
    
    material.needsUpdate = true;
  }, [materialConfig]);

  // 初始化模型
  useEffect(() => {
    if (!clonedScene) {
      console.log('克隆场景为空，跳过初始化');
      return;
    }

    try {
      console.log('开始初始化模型');
      // 设置模型变换
      clonedScene.position.set(0, 0, 0);
      clonedScene.scale.set(1, 1, 1);
      
      // 遍历优化材质和阴影
      clonedScene.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
          // 启用阴影
          child.castShadow = true;
          child.receiveShadow = true;
          
          // 优化材质
          if (child.material instanceof MeshStandardMaterial) {
            optimizeMaterial(child.material);
          }
        }
      });

      console.log('模型初始化完成');
      // 通知加载完成
      onLoad?.();
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('模型初始化失败');
      console.error('模型初始化错误:', errorObj);
      setLoadError(errorObj);
      onError?.(errorObj);
    }
  }, [clonedScene, onLoad, onError, optimizeMaterial]);

  // 清理函数
  useEffect(() => {
    return () => {
      if (!clonedScene) return;

      try {
        clonedScene.traverse((child: Object3D) => {
          if (child instanceof Mesh) {
            // 释放几何体
            if (child.geometry) {
              child.geometry.dispose();
            }

            // 释放材质
            if (child.material) {
              const materials = Array.isArray(child.material) ? child.material : [child.material];
              materials.forEach(disposeMaterial);
            }
          }
        });
      } catch (err) {
        console.error('清理资源时发生错误:', err);
      }
    };
  }, [clonedScene]);

  // 动画效果
  useFrame((state) => {
    if (group.current && visible && enableAnimation) {
      // 平滑的浮动动画
      group.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  if (loadError) {
    return (
      <Html center>
        <div className="space-y-4 text-center bg-background/90 backdrop-blur-sm p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-center gap-2 text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <span className="text-lg font-semibold">加载错误</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {loadError.message}
          </p>
        </div>
      </Html>
    );
  }

  if (!clonedScene) {
    return (
      <Html center>
        <div className="space-y-4 text-center bg-background/90 backdrop-blur-sm p-6 rounded-lg shadow-lg">
          <Progress value={progress} className="w-64" />
          <p className="text-sm text-muted-foreground">
            加载中... {progress.toFixed(0)}%
          </p>
          {errors.length > 0 && (
            <div className="mt-4 text-destructive text-sm">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4" />
                <span>加载出错</span>
              </div>
              <ul className="text-xs space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Html>
    );
  }

  return (
    <group ref={group} visible={visible} dispose={null}>
      <primitive object={clonedScene} />
    </group>
  );
};

// 预加载模型以提高性能
console.log('预加载模型:', MODEL_PATH);
useGLTF.preload(MODEL_PATH, true);

export default WuYuanWanModel; 
